import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { isBefore, isValid, startOfTomorrow } from 'date-fns';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

import { MDialog } from 'common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { INVALID_DATE, REQUIRED } from 'utils/helpers/validationMessages';
import MRadioGroup from 'feature/loading-management/create-new-common/MRadioGroup';
import Buttons from './Buttons';
import { SequencingWizardContext } from '../SequencingWizardContext';
import SummaryDrawer from './SummaryDrawer';

const options = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];

// function to test validation of transfer date
// created outside of component to keep from garbage collecting on rerenders
const getValidationMessage = R.cond([
  [R.isNil, R.always(REQUIRED)],
  [R.complement(isValid), R.always(INVALID_DATE)],
  [date => isBefore(date, startOfTomorrow()), R.always('The date entered must be in the future.')],
  [R.T, R.always('')],
]);

const FutureDate = props => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { fetchFutureSequences, futureSequences, prev, next } = props;
  const {
    data: { applyOnFutureDate, currentAffiliate, futureDate, inventoryOwners, locations, products },
    setData,
  } = useContext(SequencingWizardContext);

  const { dataCompanyId, customerIdName } = currentAffiliate;

  const [pickerDate, setPickerDate] = useState(futureDate);

  const handleForFutureChange = R.compose(
    R.o(setData, R.assoc('applyOnFutureDate')),
    R.path(['currentTarget', 'value'])
  );

  // string that holds validation message OR no value if valid
  const invalid = getValidationMessage(pickerDate);

  // picker date is set point free
  // this useEffect copies the picker date over to future date if valid
  useEffect(() => {
    if (!invalid) {
      setData(prevData => ({ ...prevData, futureDate: pickerDate }));
    } else {
      setData(prevData => ({ ...prevData, futureDate: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerDate]);

  useEffect(() => {
    fetchFutureSequences({
      companyCustomerIdName: customerIdName,
      dataCompanyId,
      inventoryOwners,
      locations,
      products,
    });
  }, [customerIdName, dataCompanyId, fetchFutureSequences, inventoryOwners, locations, products]);

  const continueDisabled = R.and(R.not(R.equals('no', applyOnFutureDate)), R.isNil(futureDate));

  const showAlert = R.and(R.equals('yes', applyOnFutureDate), R.gt(R.length(futureSequences), 0));

  return (
    <>
      <Box
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          // height of other components is 157px
          // prevents progress bar from looking off
          height: 'calc(100% - 204px)',
          marginBottom: 47, // 1px less than the summary button's height to make sure content isn't clipped
          overflowY: 'auto',
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography color="textPrimary" variant="body1">
            Would you like to apply sequence changes on a Future Date?
          </Typography>
          <MRadioGroup
            name="apply-to-future-date"
            onChange={handleForFutureChange}
            options={options}
            selection={applyOnFutureDate}
          />
          <Box sx={{ display: R.equals('yes', applyOnFutureDate) ? 'block' : 'none' }}>
            <DatePicker
              disablePast
              id="future-date"
              label="Enter date"
              minDate={startOfTomorrow()}
              onChange={setPickerDate}
              selectedDate={pickerDate}
              shouldDisableDate={date => isBefore(date, startOfTomorrow())}
              slotProps={{
                textField: {
                  placeholder: 'mm/dd/yyyy',
                  sx: { width: 1 },
                },
              }}
              sx={{
                mt: 1,
                width: '100%',
              }}
              value={pickerDate}
            />
          </Box>
          <Alert
            icon={false}
            severity="warning"
            sx={{
              '&.MuiAlert-message': {
                p: 0,
              },
              '&.MuiAlert-outlined.MuiAlert-colorWarning': {
                color: 'warning.dark',
              },
              display: showAlert ? 'revert' : 'none',
              mt: 2,
              padding: 2,
            }}
            variant="outlined"
          >
            These Products and Locations already have a Future Sequence Date and will be overwritten
            if you continue.
          </Alert>
        </Box>
        <Accordion
          defaultExpanded
          elevation={0}
          square
          sx={{
            '&.Mui-expanded': {
              '&:before': {
                opacity: 1,
              },
              '&:last-child': {
                marginBottom: 1,
              },
              margin: 0,
            },
            '&:before': {
              bottom: 0,
              top: 'auto',
            },
            display: showAlert ? 'revert' : 'none',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'secondary.main' }} />}
            sx={{
              '&.Mui-expanded': {
                margin: 0,
                minHeight: 56,
              },
              m: 0,
              minHeight: 56,
              px: 0,
              py: 3,
            }}
          >
            <Typography variant="body1">Existing Future Sequence Dates</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'block', p: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {R.map(
                ({
                  inventoryOwner,
                  locationIdName,
                  locationNameShort,
                  productIdName,
                  productNameShort,
                  type,
                }) => (
                  <Typography
                    component="div"
                    key={`${productIdName} ${productNameShort} at ${locationIdName} ${locationNameShort} ${type} ${inventoryOwner}`}
                    sx={{
                      '&:last-child': {
                        mb: 3,
                      },
                      mx: 2,
                      my: 0,
                      px: 0,
                      py: 3,
                    }}
                    variant="body1"
                  >
                    {`${productIdName} ${productNameShort} at ${locationIdName} ${locationNameShort}`}
                  </Typography>
                ),
                futureSequences
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      <SummaryDrawer displayProducts inventoryOwnerSubtitle="Primary Inventory Owner" />
      <Buttons
        disabled={continueDisabled}
        handleLeftButtonClick={prev}
        handleRightButtonClick={showAlert ? () => setDialogOpen(true) : next}
        leftLabel="Back"
        loadingData={false}
        rightLabel="Continue"
      />
      <MDialog
        header="Are you sure you want to continue?"
        leftButtonAction={() => setDialogOpen(false)}
        leftButtonText="Back"
        onClose={R.identity}
        open={dialogOpen}
        rightButtonAction={next}
        rightButtonText="Confirm"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          By confirming, you&apos;re writing over existing future sequence dates.
        </Typography>
      </MDialog>
    </>
  );
};

FutureDate.propTypes = {
  fetchFutureSequences: PropTypes.func.isRequired,
  futureSequences: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  next: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
};

export default FutureDate;
